<template>
  <Teleport to="body">
    <vue-final-modal
      v-model="showModal"
      classes="modal-container"
      content-class="modal-content copy-modal-content"
      overlay-class="vfm__overlay"
      :click-to-close="true"
      :lock-scroll="true"
      :hide-overlay="false"
      :esc-to-close="true"
      @click:overlay="closeModal"
      @click="handleOutsideClick"
    >
      <div class="p-3" v-if="orgWeek" @click.stop>
        <div class="row">
          <div class="col-md-12">
            <b>■コピー元</b><br/>
            {{dayjs(orgWeek).format('YYYY年MM月DD日')}}
            〜
            {{dayjs(orgWeek).add(6, 'days').format('YYYY年MM月DD日')}}
          </div>
        </div>
        <div class="form-body pt-4">
          <div class="form-actions">
            <div class="row">
              <div class="col-md-12 text-center">
                <input type="week" class="form-control" v-model="destWeek">
              </div>
            </div>
            <div class="row pt-2">
              <div class="col-md-12 text-center">
                <button type="button" class="btn btn-success"
                        @click="copyWeek"
                        :disabled="isProcessing">入力した週にコピーする
                </button>
              </div>
            </div>
            <div class="row pt-4">
              <div class="col-md-12 text-center">
                <input type="month" class="form-control" v-model="destMonth">
              </div>
            </div>
            <div class="row pt-2">
              <div class="col-md-12 text-center">
                <button type="button" class="btn btn-success"
                        @click="copyMonth"
                        :disabled="isProcessing">入力した月の全ての週に展開する
                </button>
              </div>
            </div>
            <div class="row pt-4">
              <div class="col-md-12">
                <b>■削除</b>
              </div>
            </div>
            <div class="row pt-2">
              <div class="col-md-12 text-center">
                <button type="button" class="btn btn-danger"
                        @click="destroy"
                        :disabled="isProcessing">
                  {{dayjs(orgWeek).format('YYYY年MM月DD日')}}<br/>
                  〜<br/>
                  {{dayjs(orgWeek).add(6, 'days').format('YYYY年MM月DD日')}}<br/>
                  を削除する
                </button>
              </div>
            </div>
            <div class="row pt-4">
              <div class="col-md-12 text-center">
                <a href="#" @click.prevent="closeModal">キャンセル</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </vue-final-modal>
  </Teleport>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import dayjs from 'dayjs'
import api from '../libs/Api'
import Constants from '../constants'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekday from 'dayjs/plugin/weekday'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(weekday)
dayjs.extend(weekOfYear)
dayjs.extend(isoWeek)

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  officeId: {
    type: [String, Number],
    default: null
  },
  userId: {
    type: [String, Number],
    default: null
  },
  weekStart: {
    type: [String, Date, Object],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'cancel'])

const showModal = ref(false)
const isProcessing = ref(false)
const orgWeek = ref(null)
const destWeek = ref(null)
const destMonth = ref(null)

const formatWeekValue = (date) => {
  if (!date) return null
  const d = dayjs(date)
  // Đảm bảo ngày bắt đầu tuần là thứ 2
  const monday = d.startOf('isoWeek')
  // Format theo đúng định dạng YYYY-Www cho input type="week"
  const weekNum = monday.isoWeek().toString().padStart(2, '0')
  const result = `${monday.format('YYYY')}-W${weekNum}`

  return result
}

watch(() => props.modelValue, (newVal) => {
  showModal.value = newVal
  if (newVal && props.weekStart) {
    const startDate = dayjs(props.weekStart)
    // 週を月曜始まりに正規化（表示とAPIの週範囲を一致させる）
    orgWeek.value = startDate.startOf('isoWeek')
    // Set destWeek là tuần tiếp theo
    destWeek.value = formatWeekValue(startDate.add(1, 'week'))
    destMonth.value = startDate.format('YYYY-MM')
  }
})

watch(() => destWeek.value, (newVal) => {
}, { immediate: true })

watch(() => showModal.value, (newVal) => {
  emit('update:modelValue', newVal)
})

const closeModal = () => {
  emit('cancel')
  emit('update:modelValue', false)
}

const copyWeek = () => {
  if (isProcessing.value) return
  if (!destWeek.value) {
    alert('コピーする週を入力してください。')
    return
  }

  if (!confirm('コピーします。よろしいですか？')) {
    return
  }

  // Xử lý chuỗi YYYY-Www thành year và week
  const [yearStr, weekStr] = destWeek.value.split('-W')
  const year = parseInt(yearStr)
  const week = parseInt(weekStr)

  // Sử dụng isoWeek để đảm bảo tuần bắt đầu từ thứ 2
  const targetWeek = dayjs().year(year).isoWeek(week).startOf('isoWeek')

  _copy(targetWeek.format('YYYYMMDD'), Constants.PLAN_COPY_TYPE_WEEK)
}

const copyMonth = () => {
  if (isProcessing.value) return
  if (!destMonth.value) {
    alert('展開する月を入力してください。')
    return
  }

  if (!confirm('コピーします。よろしいですか？')) {
    return
  }

  const targetMonth = dayjs(destMonth.value, 'YYYY-MM')
  _copy(targetMonth.startOf('month').format('YYYYMMDD'), Constants.PLAN_COPY_TYPE_REPEAT_MONTH)
}

const _copy = async (dst, planCopyType) => {
  try {
    isProcessing.value = true
    // 週を月曜始まりに正規化（API の週範囲と一致）
    const srcStartOfWeek = dayjs(orgWeek.value).startOf('isoWeek').format('YYYYMMDD')
    await api.copyPlan(props.officeId, props.userId, srcStartOfWeek, dst, planCopyType)
    emit('update:modelValue', false)
    // コピー元の週を渡し、コピー後にその範囲で再取得できるようにする
    emit('change', dst, orgWeek.value)
  } catch (error) {
    console.error(error)
  } finally {
    isProcessing.value = false
  }
}

const destroy = () => {
  // TODO: Implement dialog confirmation using vue-final-modal
  if (confirm('削除してもよろしいですか？')) {
    destroyPlan()
  }
}

const destroyPlan = async () => {
  try {
    isProcessing.value = true
    const fromDestroyWeek = dayjs(orgWeek.value).format('YYYYMMDD')
    const toDestroyWeek = dayjs(orgWeek.value).add(6, 'day').format('YYYYMMDD')

    await api.deleteRangePlan(props.officeId, props.userId, fromDestroyWeek, toDestroyWeek)
    emit('update:modelValue', false)
    emit('change')
  } catch (error) {
    console.error(error)
  } finally {
    isProcessing.value = false
  }
}

const handleOutsideClick = (event) => {
  if (event.target.classList.contains('vfm__overlay')) {
    closeModal()
  }
}
</script>

<style>
/* Remove scoped to allow styles to affect child components */
.vfm__container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.copy-modal {
  width: 350px !important;
  min-height: 600px !important;
  margin: 49px auto !important;
  position: relative !important;
  background: white;
  border-radius: 4px;
  padding: 20px;
  overflow-y: auto;
}

.actions {
  background-color: white;
  position: fixed;
  bottom: 0;
  width: calc(100% - 40px);
  padding: 15px;
  border-top: 1px solid #e2e8f0;
  margin-left: -20px;
}

@media (max-width: 1920px) {
  .copy-modal-content {
    width: 350px !important;
    height: 600px !important;
  }
}
</style>
